import { useState } from "react";
import type { FormEvent } from "react";

import { isAPIError } from "@/core/services";
import { useDialogForm } from "@/ds";
import { cadastrarCurva, editarCurva } from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { CATEGORIA_OPCOES_CADASTRO } from "../curvas.constants";
import { formatPercentual } from "../curvas.utils";
import {
  CURVA_FORM_ID,
  FASE_ANTES_APOIO,
  FASE_ANTES_TITULO,
  FASE_APOS_TITULO,
  FASE_DURANTE_APOIO,
  FASE_DURANTE_TITULO,
  NOME_MAX,
  SOMA_INVALIDA,
  TITULO_EDICAO,
  TITULO_NOVA,
  totalMesesTexto,
  usoTexto,
} from "./curva-dialog.constants";
import {
  CURVA_VAZIA,
  erroCategoria,
  erroMeses,
  erroMesesObrigatorio,
  erroNome,
  erroPercentual,
  formDaCurva,
  montarCorpoEdicao,
  montarCorpoNova,
  podeSalvar,
  previaDoForm,
  somaPercentuais,
  somaValida,
  totalMeses,
} from "./curva-dialog.form";
import type { CurvaForm } from "./curva-dialog.form";
import type { CampoCurva, CurvaDialogProps, CurvaDialogViewModel } from "./types-curva-dialog";

type CampoNumerico = Exclude<keyof CurvaForm, "nome" | "categoria">;

export function useCurvaDialog({
  modo,
  curva,
  onClose,
  onConcluido,
}: CurvaDialogProps): CurvaDialogViewModel {
  const editando = modo === "edicao" && curva !== undefined;
  const { comChave } = useChaveIdempotencia();
  const [nomeDoServidor, setNomeDoServidor] = useState<string | null>(null);

  const form = useDialogForm<CurvaForm>({
    initial: () => (editando ? formDaCurva(curva) : CURVA_VAZIA),
    validate: ({ nome, categoria }) => ({
      nome: erroNome(nome) ?? undefined,
      ...(editando ? {} : { categoria: erroCategoria(categoria) ?? undefined }),
    }),
    onSubmit: async (valores) => {
      if (!podeSalvar(valores)) return;
      try {
        if (editando) {
          const corpo = montarCorpoEdicao(valores);
          await comChave(corpo, (idempotencyKey) =>
            editarCurva(curva.id, corpo, { idempotencyKey }),
          );
        } else if (valores.categoria) {
          const corpo = montarCorpoNova(valores, valores.categoria);
          await comChave(corpo, (idempotencyKey) => cadastrarCurva(corpo, { idempotencyKey }));
        }
      } catch (erro) {
        if (isAPIError(erro) && erro.status === 409) {
          setNomeDoServidor(erro.message);
          return;
        }
        throw erro;
      }
      onConcluido();
    },
  });

  const { values } = form;
  const descarte = useConfirmarDescarte({ onDescartar: onClose, testId: "curva-descarte" });

  const campo = (
    chave: CampoNumerico,
    validar: (valor: number | null) => string | null,
  ): CampoCurva => ({
    valor: values[chave],
    erro: validar(values[chave]),
    testId: `curva-${chave}`,
    digitar: (valor) => form.setValue(chave, valor),
  });

  const soma = somaPercentuais(values);
  const valida = somaValida(soma);
  const salvarHabilitado = valida;
  const salvarAviso = salvarHabilitado ? null : SOMA_INVALIDA;

  const enviar = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    if (salvarHabilitado) void form.submit();
  };

  return {
    formId: CURVA_FORM_ID,
    titulo: editando ? TITULO_EDICAO : TITULO_NOVA,
    nome: {
      valor: values.nome,
      max: NOME_MAX,
      erro: nomeDoServidor ?? form.errors.nome ?? null,
      digitar: (valor) => {
        setNomeDoServidor(null);
        form.setValue("nome", valor);
      },
    },
    categoria: {
      valor: values.categoria,
      opcoes: [...CATEGORIA_OPCOES_CADASTRO],
      desabilitado: editando || form.isSubmitting,
      erro: form.errors.categoria ?? null,
      escolher: (categoria) => {
        setNomeDoServidor(null);
        form.setValue("categoria", categoria);
      },
    },
    fases: [
      {
        titulo: FASE_ANTES_TITULO,
        apoio: FASE_ANTES_APOIO,
        percentual: campo("percentualAntes", erroPercentual),
        meses: campo(
          "mesesAntes",
          (meses) =>
            erroMeses(meses) ?? erroMesesObrigatorio(values.percentualAntes, meses, "antes"),
        ),
      },
      {
        titulo: FASE_DURANTE_TITULO,
        apoio: FASE_DURANTE_APOIO,
        percentual: campo("percentualDurante", erroPercentual),
        meses: null,
      },
      {
        titulo: FASE_APOS_TITULO,
        apoio: null,
        percentual: campo("percentualApos", erroPercentual),
        meses: campo(
          "mesesApos",
          (meses) => erroMeses(meses) ?? erroMesesObrigatorio(values.percentualApos, meses, "apos"),
        ),
      },
    ],
    soma: {
      texto: `Soma: ${formatPercentual(soma)}`,
      valida,
      mensagem: valida ? totalMesesTexto(totalMeses(values)) : SOMA_INVALIDA,
    },
    uso:
      editando && curva.uso.escopos + curva.uso.obras > 0
        ? usoTexto(curva.uso.escopos, curva.uso.obras)
        : null,
    previa: previaDoForm(values),
    nomeDaCurva: values.nome.trim() || (editando ? curva.nome : TITULO_NOVA),
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    salvarDesabilitado: !salvarHabilitado,
    salvarAviso,
    enviar,
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
  };
}
